import * as React from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Divider,
  IconButton,
  Link,
  Stack,
  Typography,
  Chip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTitle } from "../../hooks/useTitle";
import {
  useDeleteThreadCommentMutation,
  useGetForumThreadByIdQuery,
  useGetForumThreadCommentsQuery,
  useLockForumThreadMutation,
  usePinForumThreadMutation,
} from "../../services/forum.service";
import dayjs from "dayjs";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import { ThreadComment } from "../../types/forum";
import ConfirmDeleteModal from "../../components/ConfirmDeleteModal";
import { toast } from "react-toastify";
import PushPinOutlined from "@mui/icons-material/PushPinOutlined";
import LockOutlined from "@mui/icons-material/LockOutlined";
import { LoadingButton } from "@mui/lab";
import { decode } from "html-entities";
import { CardBase, Button, OrangeLinearProgress } from "../../components/global";

const CardComment = ({
  comment,
  onDelete,
}: {
  comment: ThreadComment;
  onDelete: (comment: ThreadComment) => void;
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  return (
    <CardBase
      useGlassCard={false}
      sx={{ mt: 2, mb: 1 }}
      headerActions={
        <IconButton
          size="small"
          onClick={() => onDelete(comment)}
          sx={{
            color: "#d32f2f",
            "&:hover": {
              backgroundColor: "rgba(211, 47, 47, 0.1)",
            },
          }}
        >
          <DeleteOutline fontSize={isMobile ? "small" : "medium"} />
        </IconButton>
      }
    >
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate(`/talents/${comment.owner?.id}`)}
            sx={{
              color: "#F4A259",
              fontWeight: 600,
              textDecoration: "none",
              fontSize: isMobile ? "0.75rem" : "0.875rem",
              "&:hover": {
                textDecoration: "underline",
                color: "#E8944A",
              },
            }}
          >
            {comment.owner?.full_name}
          </Link>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? "0.65rem" : "0.75rem" }}>
            #{comment?.id} • {dayjs(comment?.created_at).format("MMM DD YYYY, hh:mm A")}
          </Typography>
        </Stack>
        <Box
          sx={{
            whiteSpace: "pre-wrap",
            overflowWrap: "break-word",
            color: "text.primary",
            fontSize: isMobile ? "0.8rem" : "0.9rem",
            lineHeight: 1.6,
          }}
        >
          {decode(comment.comment)}
        </Box>
        {(comment?.documents?.length || 0) > 0 && (
          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: "#F4A259", fontSize: isMobile ? "0.75rem" : "0.875rem" }}>
              Attachments:
            </Typography>
            <Stack spacing={0.5}>
              {comment?.documents?.map((doc, index) => (
                <Link
                  key={index}
                  href={doc.original_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: "#F4A259",
                    textDecoration: "none",
                    fontSize: isMobile ? "0.7rem" : "0.875rem",
                    "&:hover": {
                      textDecoration: "underline",
                      color: "#E8944A",
                    },
                  }}
                >
                  {doc.file_name}
                </Link>
              ))}
            </Stack>
          </Box>
        )}
        {(comment.childs?.length || 0) > 0 && (
          <Box sx={{ pl: isMobile ? 1 : 2, borderLeft: "2px solid rgba(244, 162, 89, 0.2)", mt: 1 }}>
            {comment.childs?.map((child, index) => (
              <CardComment key={child.id || index} comment={child} onDelete={onDelete} />
            ))}
          </Box>
        )}
      </Stack>
    </CardBase>
  );
};

const SocialFeedDetailPage = () => {
  const { threadId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    data: thread,
    isLoading,
    isFetching,
  } = useGetForumThreadByIdQuery(parseInt(threadId!), {
    skip: !threadId,
  });

  const {
    data: comments,
    isLoading: loadingComments,
    isFetching: fetchingComments,
  } = useGetForumThreadCommentsQuery(
    { thread_id: thread?.id! },
    {
      skip: !thread,
    }
  );

  const [doDelete, { isLoading: deleting }] = useDeleteThreadCommentMutation();
  const [doLock, { isLoading: locking }] = useLockForumThreadMutation();
  const [doPin, { isLoading: pinning }] = usePinForumThreadMutation();

  const [deleteModal, setDeleteModal] = React.useState<{
    open: boolean;
    row: ThreadComment | null;
  }>({
    open: false,
    row: null,
  });

  const closeDeleteModal = () =>
    setDeleteModal({ ...deleteModal, open: false });

  const handleDelete = async () => {
    if (deleteModal.row?.id) {
      try {
        await doDelete({
          thread_id: parseInt(threadId!),
          comment_id: deleteModal.row.id,
        }).unwrap();
        closeDeleteModal();
        toast.success("Successfully deleted");
      } catch (err) {
        closeDeleteModal();
      }
    }
  };

  useTitle(`Feed #${thread?.id}`, `${thread?.title} | Feed`);

  if (isLoading || isFetching) {
    return (
      <Box>
        <OrangeLinearProgress />
      </Box>
    );
  }

  if (!thread) {
    return (
      <CardBase
        title="Thread Not Found"
        useGlassCard={false}
        sx={{ minHeight: "400px" }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "300px",
            color: "text.secondary",
          }}
        >
          Thread not found
        </Box>
      </CardBase>
    );
  }

  return (
    <Box>
      <CardBase
        title={decode(thread?.title) || ""}
        useGlassCard={false}
        sx={{ mb: 2 }}
        headerActions={
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ width: isMobile ? "100%" : "auto" }}>
            <LoadingButton
              size="small"
              startIcon={<LockOutlined />}
              variant={thread?.is_locked ? "contained" : "outlined"}
              loading={locking}
              onClick={() =>
                doLock({ thread_id: thread?.id!, lock: !thread?.is_locked })
              }
              fullWidth={isMobile}
              sx={{
                ...(thread?.is_locked
                  ? {
                    backgroundColor: "#d32f2f",
                    color: "#ffffff",
                    "&:hover": {
                      backgroundColor: "#b71c1c",
                    },
                  }
                  : {
                    color: "#F4A259",
                    borderColor: "#F4A259",
                    "&:hover": {
                      borderColor: "#F4A259",
                      backgroundColor: "rgba(244, 162, 89, 0.08)",
                    },
                  }),
              }}
            >
              {thread?.is_locked ? (isMobile ? "Un-Lock" : "Un-Lock Thread") : (isMobile ? "Lock" : "Lock Thread")}
            </LoadingButton>
            <LoadingButton
              size="small"
              startIcon={<PushPinOutlined />}
              variant={thread?.is_pinned ? "contained" : "outlined"}
              loading={pinning}
              onClick={() =>
                doPin({ thread_id: thread?.id!, pin: !thread?.is_pinned })
              }
              fullWidth={isMobile}
              sx={{
                ...(thread?.is_pinned
                  ? {
                    backgroundColor: "#F4A259",
                    color: "#ffffff",
                    "&:hover": {
                      backgroundColor: "#E8944A",
                    },
                  }
                  : {
                    color: "#F4A259",
                    borderColor: "#F4A259",
                    "&:hover": {
                      borderColor: "#F4A259",
                      backgroundColor: "rgba(244, 162, 89, 0.08)",
                    },
                  }),
              }}
            >
              {thread?.is_pinned ? (isMobile ? "Un-Pin" : "Un-Pin Thread") : (isMobile ? "Pin" : "Pin Thread")}
            </LoadingButton>
          </Stack>
        }
      >
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Link
              component="button"
              variant="body2"
              onClick={() => navigate(`/talents/${thread?.user?.id}`)}
              sx={{
                color: "#F4A259",
                fontWeight: 600,
                textDecoration: "none",
                fontSize: isMobile ? "0.75rem" : "0.875rem",
                "&:hover": {
                  textDecoration: "underline",
                  color: "#E8944A",
                },
              }}
            >
              {thread?.user?.full_name}
            </Link>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: isMobile ? "0.7rem" : "0.875rem" }}>
              •
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: isMobile ? "0.7rem" : "0.875rem" }}>
              {dayjs(thread?.created_at).format("MMM DD YYYY, hh:mm A")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: isMobile ? "0.7rem" : "0.875rem" }}>
              •
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: isMobile ? "0.7rem" : "0.875rem" }}>
              {thread?.total_view || 0} views
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: isMobile ? "0.7rem" : "0.875rem" }}>
              •
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: isMobile ? "0.7rem" : "0.875rem" }}>
              {thread?.total_likes || 0} likes
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: isMobile ? "0.7rem" : "0.875rem" }}>
              •
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: isMobile ? "0.7rem" : "0.875rem" }}>
              {thread?.total_comments || 0} comment(s)
            </Typography>
          </Stack>
          <Divider />
          <Box
            sx={{
              whiteSpace: "pre-wrap",
              overflowWrap: "break-word",
              color: "text.primary",
              fontSize: isMobile ? "0.85rem" : "0.95rem",
              lineHeight: 1.7,
            }}
          >
            {decode(thread?.body) || ""}
          </Box>
          {thread?.tags && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: "#F4A259", fontSize: isMobile ? "0.75rem" : "0.875rem" }}>
                Tags:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {thread.tags.split(",").map((tag, index) => (
                  <Chip
                    key={index}
                    label={tag.trim()}
                    size={isMobile ? "small" : "medium"}
                    sx={{
                      backgroundColor: "rgba(244, 162, 89, 0.08)",
                      color: "#F4A259",
                      border: "1px solid rgba(244, 162, 89, 0.2)",
                      fontSize: isMobile ? "0.7rem" : "0.75rem",
                    }}
                  />
                ))}
              </Stack>
            </Box>
          )}
          {(thread?.documents?.length || 0) > 0 && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: "#F4A259", fontSize: isMobile ? "0.75rem" : "0.875rem" }}>
                Attachments:
              </Typography>
              <Stack spacing={0.5}>
                {thread?.documents?.map((doc, index) => (
                  <Link
                    key={index}
                    href={doc.original_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: "#F4A259",
                      textDecoration: "none",
                      fontSize: isMobile ? "0.7rem" : "0.875rem",
                      "&:hover": {
                        textDecoration: "underline",
                        color: "#E8944A",
                      },
                    }}
                  >
                    {doc.file_name}
                  </Link>
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      </CardBase>

      <CardBase
        title="Comments"
        useGlassCard={false}
        sx={{ mb: 2 }}
      >
        {(loadingComments || fetchingComments) && (
          <Box sx={{ mb: 2 }}>
            <OrangeLinearProgress />
          </Box>
        )}
        {comments && comments.length > 0 ? (
          <Stack spacing={1}>
            {comments.map((comment, index) => (
              <CardComment
                key={comment.id || index}
                comment={comment}
                onDelete={(data: ThreadComment) =>
                  setDeleteModal({ open: true, row: data })
                }
              />
            ))}
          </Stack>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "200px",
              color: "text.secondary",
            }}
          >
            No comments yet
          </Box>
        )}
      </CardBase>

      <ConfirmDeleteModal
        open={deleteModal.open}
        onClose={closeDeleteModal}
        onDelete={handleDelete}
        message={`Delete comment #${deleteModal.row?.id} by ${deleteModal.row?.owner?.full_name} ?`}
      />
    </Box>
  );
};
export default SocialFeedDetailPage;
